import { FC, useEffect, useRef, useState } from 'react';

import { useTowerLayout } from '../../../../../TowerLayout/TowerLayoutProvider';
import SmartPoliciesTable from './SmartPoliciesTable';
import SmartPolicyDrawer from './SmartPolicyDrawer/SmartPolicyDrawer';
import {
    Button,
    ButtonStyles,
    DefaultContentComponent,
    DefaultFooterComponent,
    Icon,
    IconButton,
    IconButtonStyles,
    Icons,
    Popup,
    SnackAlert
} from '@silverfort/sf-components';
import { useSmartPolicyContext } from './SmartPolicyContext';
import { SmartPolicyCreationFlow } from './SmartPoliciesTypes';
import useHasPermissions from '../../../../../hooks/useHasPermissions';
import { Pages, Roles } from '../../../../../context/contexts/UserContext/permissionsTypes';
import useGetRefSize from '../../../../../hooks/useGetRefSize';

type SmartPoliciesProps = {
    returnToMainPage: () => void;
};

const SmartPolicies: FC<SmartPoliciesProps> = ({ returnToMainPage }) => {
    const { setLayoutHeader, setLayoutContentLeft, setLayoutContentIcon, clearLayout } = useTowerLayout();
    const {
        smartPoliciesConfig,
        createNewSmartPolicy,
        smartPolicies,
        policyToDelete,
        setPolicyToDelete,
        deletePolicy,
        snackAlert,
        setSnackAlert
    } = useSmartPolicyContext();
    const tableRef = useRef(null);
    const { height: tableHeight } = useGetRefSize(tableRef);

    const [showMaxPoliciesBanner, setShowMaxPoliciesBanner] = useState(true);
    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.EDITOR });

    useEffect(() => {
        setLayoutHeader('Smart Policies');
        setLayoutContentLeft(
            <IconButton buttonStyle={IconButtonStyles.Secondary} icon={Icons.ArrowBack} onClick={returnToMainPage} />
        );
        setLayoutContentIcon(<Icon icon={Icons.SmartPolicy} size={20} />);
        return clearLayout;
    }, [returnToMainPage, setLayoutContentIcon, setLayoutContentLeft, setLayoutHeader, clearLayout]);

    const maxPoliciesReached = !smartPoliciesConfig || smartPolicies.length >= smartPoliciesConfig.maxSmartPolicies;

    return (
        <div className="h-[calc(100vh-64px)] w-[calc(100vw-64px)] ml-16 mt-16 bg-white flex flex-col">
            {showMaxPoliciesBanner && isEditor && (
                <div className="p-4 border-b border-sf-blue bg-[#F5FAFF] flex justify-between items-center">
                    <div className="flex gap-1 items-center">
                        <Icon icon={Icons.CircleInfo} className="text-sf-blue" />
                        <span className="text-xs font-medium">You can create up to 10 smart policy rules.</span>
                    </div>
                    <div className="flex items-center">
                        <IconButton
                            buttonStyle={IconButtonStyles.Secondary}
                            icon={Icons.Cancel}
                            onClick={() => setShowMaxPoliciesBanner(false)}
                        />
                    </div>
                </div>
            )}
            <div className="p-6 flex flex-1 flex-col gap-6">
                {isEditor && (
                    <div className="flex justify-end">
                        <div>
                            <Button
                                text={smartPolicies.length === 0 ? 'Create Policy' : 'Add'}
                                tooltip={
                                    maxPoliciesReached
                                        ? {
                                              title: `You can create up to ${smartPoliciesConfig?.maxSmartPolicies} smart policy rules`
                                          }
                                        : undefined
                                }
                                onClick={() => createNewSmartPolicy({ creationFlow: SmartPolicyCreationFlow.MANUAL })}
                                disabled={maxPoliciesReached}
                            />
                        </div>
                    </div>
                )}
                <div ref={tableRef} className="flex-1">
                    <SmartPoliciesTable height={tableHeight} />
                </div>
            </div>
            <Popup
                open={!!policyToDelete}
                title="Delete Smart Policy Rule"
                ContentComponent={DefaultContentComponent}
                ContentComponentProps={{
                    text: `This will remove the rule only. Policies it created or modified will stay in place. To make changes, update or delete those policies directly.`
                }}
                FooterComponent={DefaultFooterComponent}
                FooterComponentProps={{
                    primaryButtonProps: {
                        text: 'Delete',
                        buttonStyle: ButtonStyles.DangerSolid
                    },
                    secondaryButtonProps: {
                        text: 'Cancel',
                        buttonStyle: ButtonStyles.TertiarySolid
                    }
                }}
                onApprove={deletePolicy}
                onClose={() => setPolicyToDelete(undefined)}
                popupClassName="w-[360px] h-[240px]"
            />
            {snackAlert && (
                <SnackAlert
                    anchorOrigin={{
                        horizontal: 'center',
                        vertical: 'bottom'
                    }}
                    sx={{
                        '& .MuiSnackbarContent-root': {
                            minWidth: 'auto'
                        }
                    }}
                    message={snackAlert}
                    autoHideDuration={3000}
                    onClose={() => setSnackAlert(undefined)}
                />
            )}
            <SmartPolicyDrawer />
        </div>
    );
};

export default SmartPolicies;
